/**
 * Utility functions for handling TED procedure types
 */
import { 
  NOTICE_SUBTYPES, 
  getNoticeSubtypeByNoticeType, 
  getNoticeSubtypesByNoticeType, 
  getNoticeSubtypeById,
  getLegalBasisDescription,
  getFormTypeDescription,
  getDocumentTypeDescription
} from "./noticeSubtypeData";

export type ProcedureTypeCode = 
  | 'comp-dial'
  | 'comp-tend'
  | 'innovation'
  | 'neg-w-call'
  | 'neg-wo-call'
  | 'open'
  | 'oth-mult'
  | 'oth-single'
  | 'restricted'
  | 'dps'
  | string;

export const PROCEDURE_TYPES: Record<ProcedureTypeCode, string> = {
  'comp-dial': 'Competitive Dialogue',
  'comp-tend': 'Competitive Tendering (article 5(3) of Regulation 1370/2007)',
  'innovation': 'Innovation Partnership',
  'neg-w-call': 'Negotiated with prior publication of a call for competition / competitive with negotiation',
  'neg-wo-call': 'Negotiated without prior call for competition',
  'open': 'Open Procedure',
  'oth-mult': 'Other Multiple Stage Procedure',
  'oth-single': 'Other Single Stage Procedure',
  'restricted': 'Restricted Procedure',
  'dps': 'Dynamic Purchasing System',
  '[1]': 'Accelerated negotiated procedure',
  '[4]': 'Negotiated procedure',
  '[6]': 'Accelerated negotiated procedure',
  '[8]': 'Other',
  '[9]': 'Not applicable',
  '[A]': 'Direct award',
  '[E]': 'Concession award procedure',
  '[F]': 'Concession award without prior concession notice',
  '[V]': 'Contract award without prior publication',
  '[Z]': 'Not specified',
  '[exp-int-rail]': 'Request for expression of interest – only for rail (article 5(3b) of Regulation 1370/2007)',
};

export const PROCEDURE_DESCRIPTIONS: Record<ProcedureTypeCode, string> = {
  'comp-dial': 'A procedure where the contracting authority enters into a dialogue with selected candidates before final submission.',
  'comp-tend': 'A fully competitive process where all interested parties are invited to submit tenders under article 5(3) of Regulation 1370/2007.',
  'innovation': 'A procedure focused on developing innovative solutions in partnership with suppliers for new products or services not yet available on the market.',
  'neg-w-call': 'A negotiation process that starts with a public call to invite interested candidates before negotiating contract details.',
  'neg-wo-call': 'A direct negotiation process conducted without a prior public call for competition, typically used in special circumstances.',
  'open': 'The standard process where any supplier may submit a tender.',
  'oth-mult': 'A non-standard process that results in multiple awards.',
  'oth-single': 'A non-standard process that results in a single award.',
  'restricted': 'A process where only a pre-selected group of candidates is invited to tender.',
  'dps': 'An electronic system allowing continuous qualification of suppliers for routine or repetitive procurements.',
  '[1]': 'An accelerated version of the negotiated procedure with reduced timelines.',
  '[4]': 'A procedure where the contracting authority consults selected suppliers and negotiates the terms of contract with one or more of them.',
  '[6]': 'An accelerated version of the negotiated procedure with reduced timelines.',
  '[8]': 'Other type of procedure not listed in standard categories.',
  '[9]': 'No procedure type applicable to this notice.',
  '[A]': 'Direct award to a supplier without competition.',
  '[E]': 'A procedure for awarding concession contracts.',
  '[F]': 'Concession award made without prior publication of a concession notice.',
  '[V]': 'Contract awarded without prior publication of a contract notice.',
  '[Z]': 'Procedure type not specified in the notice.',
  '[exp-int-rail]': 'Special procedure for rail services under article 5(3b) of Regulation 1370/2007.',
};

export const NOTICE_TYPES: Record<string, string> = {
  // Contract notices
  'cn-standard': 'Contract or concession notice – standard regime',
  'cn-social': 'Contract notice – light regime',
  'cn-desg': 'Design contest notice',
  
  // Award notices
  'can-standard': 'Contract or concession award notice – standard regime',
  'can-social': 'Contract or concession award notice – light regime',
  'can-desg': 'Design contest result notice', 
  'can-modif': 'Contract modification notice',
  'can-tran': 'Contract award notice for public passenger transport services',
  
  // Prior information notices
  'pin-only': 'Prior information notice or a periodic indicative notice used only for information',
  'pin-cfc-standard': 'Prior information notice or a periodic indicative notice used as a call for competition – standard regime',
  'pin-cfc-social': 'Prior information notice or a periodic indicative notice used as a call for competition – light regime',
  'pin-rtl': 'Prior information notice or a periodic indicative notice used to shorten time limits for receipt of tenders',
  'pin-buyer': 'Notice of the publication of a prior information notice on a buyer profile',
  'pin-tran': 'Prior information notice for public passenger transport services',
  
  // Other notice types
  'corr': 'Change notice',
  'compl': 'Contract completion notice',
  'qu-sy': 'Notice on the existence of a qualification system',
  'pmc': 'Pre-market consultation notice',
  'subco': 'Subcontracting notice',
  'veat': 'Voluntary ex-ante transparency notice',
  'brin-eeig': 'European Economic Interest Grouping notice',
  'brin-ecs': 'European Company / European Cooperative Society notice',
  
  // Legacy
  'result': 'Contract Award Notice'
};

// Detailed descriptions for notice types
export const NOTICE_TYPE_DESCRIPTIONS: Record<string, string> = {
  // Contract notices
  'cn-standard': 'Standard procurement notice for contracts or concessions, announcing a new procurement opportunity.',
  'cn-social': 'A contract notice under the simplified "light regime" for certain public contracts, particularly social services.',
  'cn-desg': 'A notice for a design contest related to public procurement, inviting designers to participate.',
  
  // Award notices
  'can-standard': 'Standard award notice for contracts or concessions, announcing the winner of a procurement process.',
  'can-social': 'Notifies the award of a contract or concession under the light regime for social and specific services.',
  'can-desg': 'Announcing the result of a design contest, including the winner and any prizes awarded.', 
  'can-modif': 'Notifying modifications to an existing contract, such as extensions or significant changes.',
  'can-tran': 'Announcing the award of a contract for public passenger transport services.',
  
  // Prior information notices
  'pin-only': 'A notice solely for informational purposes, alerting the market to future procurement plans.',
  'pin-cfc-standard': 'Notice for competition calls under the standard procurement regime, used as a call for competition.',
  'pin-cfc-social': 'Prior notice indicating an upcoming competition under the light regime for social and specific services.',
  'pin-rtl': 'Notice to shorten the time limits for tender submissions in upcoming procurement procedures.',
  'pin-buyer': 'Information about prior or periodic updates on a buyer profile, directing suppliers to additional information.',
  'pin-tran': 'Early notification about upcoming tenders for public passenger transport services.',
  
  // Other notice types
  'corr': 'Used to notify changes in a previously published procurement notice or to correct errors.',
  'compl': 'Informing about the completion of a contract, including final details and outcomes.',
  'qu-sy': 'Announcing a qualification system for contractors or suppliers, allowing pre-qualification for future tenders.',
  'pmc': 'A notice to consult the market before launching a formal procurement procedure, gathering input from potential suppliers.',
  'subco': 'Announcing opportunities or conditions for subcontracting in public contracts.',
  'veat': 'A notice providing early voluntary transparency in the procurement process, prior to contract award.',
  'brin-eeig': 'Relates to the formation of European Economic Interest Groupings, a type of legal entity.',
  'brin-ecs': 'For the creation of European Companies or European Cooperative Societies under EU law.',
  
  // Legacy
  'result': 'Contract Award Notice, announcing the successful tenderer in a procurement process.'
};

/**
 * Categorize notice types for icon selection
 */
export const NOTICE_TYPE_CATEGORIES: Record<string, 'award' | 'contract' | 'prior' | 'change' | 'other'> = {
  // Contract notices
  'cn-standard': 'contract',
  'cn-social': 'contract',
  'cn-desg': 'contract',
  
  // Award notices
  'can-standard': 'award',
  'can-social': 'award',
  'can-desg': 'award',
  'can-modif': 'change',
  'can-tran': 'award',
  
  // Prior information notices
  'pin-only': 'prior',
  'pin-cfc-standard': 'prior',
  'pin-cfc-social': 'prior',
  'pin-rtl': 'prior',
  'pin-buyer': 'prior',
  'pin-tran': 'prior',
  
  // Other notice types
  'corr': 'change',
  'compl': 'other',
  'qu-sy': 'other',
  'pmc': 'other',
  'subco': 'other',
  'veat': 'other',
  'brin-eeig': 'other',
  'brin-ecs': 'other',
  
  // Legacy
  'result': 'award'
};

/**
 * Get the human-readable description of a procedure type
 */
export function getProcedureTypeDescription(code: ProcedureTypeCode | undefined | null): string {
  if (!code) return 'Unknown procedure type';
  
  // Check if there's a direct match in our PROCEDURE_TYPES mapping
  if (PROCEDURE_TYPES[code]) {
    return PROCEDURE_TYPES[code];
  }
  
  // Try to extract code if it's in brackets like [open]
  if (typeof code === 'string' && code.match(/\[(.*?)\]/)) {
    const extractedCode = code.match(/\[(.*?)\]/)?.[1];
    if (extractedCode && PROCEDURE_TYPES[`[${extractedCode}]`]) {
      return PROCEDURE_TYPES[`[${extractedCode}]`];
    }
  }
  
  return `Unknown procedure (${code})`;
}

/**
 * Get the detailed description of a procedure type
 */
export function getProcedureDetailedDescription(code: ProcedureTypeCode | undefined | null): string {
  if (!code) return '';
  
  // Check if there's a direct match in our PROCEDURE_DESCRIPTIONS mapping
  if (PROCEDURE_DESCRIPTIONS[code]) {
    return PROCEDURE_DESCRIPTIONS[code];
  }
  
  // Try to extract code if it's in brackets like [open]
  if (typeof code === 'string' && code.match(/\[(.*?)\]/)) {
    const extractedCode = code.match(/\[(.*?)\]/)?.[1];
    if (extractedCode && PROCEDURE_DESCRIPTIONS[`[${extractedCode}]`]) {
      return PROCEDURE_DESCRIPTIONS[`[${extractedCode}]`];
    }
  }
  
  return '';
}

/**
 * Check if a notice is a contract award notice
 */
export function isContractAwardNotice(tender: any): boolean {
  if (!tender) return false;
  
  // Check for "BT-03-notice": "result"
  if (tender["BT-03-notice"] === "result") return true;
  
  // Check for notice types that indicate award notices
  const noticeType = tender["notice-type"];
  if (noticeType && NOTICE_TYPE_CATEGORIES[noticeType] === 'award') {
    return true;
  }
  
  // Legacy check
  if (typeof noticeType === 'string' && noticeType.toLowerCase().includes('award')) {
    return true;
  }
  
  return false;
}

/**
 * Get procedure type from tender data
 */
export function getProcedureType(tender: any): ProcedureTypeCode | null {
  if (!tender) return null;
  
  // Check for "PR" field
  if (tender.PR) return tender.PR;
  
  // Check for procedure-type
  if (tender["procedure-type"]) return tender["procedure-type"];
  
  // Check for TOP_TYPE field (older format)
  if (tender.TOP_TYPE) return tender.TOP_TYPE;
  
  return null;
}

/**
 * Get notice type description from notice type code
 */
export function getNoticeTypeDescription(code: string | undefined | null): string {
  if (!code) return 'Unknown notice type';
  return NOTICE_TYPES[code] || `Unknown notice (${code})`;
}

/**
 * Get notice type detailed description from notice type code
 */
export function getNoticeTypeDetailedDescription(code: string | undefined | null): string {
  if (!code) return '';
  return NOTICE_TYPE_DESCRIPTIONS[code] || '';
}

/**
 * Get notice type category for icon selection
 */
export function getNoticeTypeCategory(code: string | undefined | null): string {
  if (!code) return 'other';
  return NOTICE_TYPE_CATEGORIES[code] || 'other';
}

/**
 * Get notice subtype ID from tender data
 */
export function getNoticeSubtypeId(tender: any): string | null {
  if (!tender) return null;
  
  // Check for "notice-subtype" field
  if (tender["notice-subtype"]) return tender["notice-subtype"];
  
  // Check for numeric notice-subtype field
  if (tender["notice-subtype-id"]) return tender["notice-subtype-id"];
  
  return null;
}

/**
 * Re-export functions from noticeSubtypeData to make them available to consumers
 */
export { 
  getNoticeSubtypeById,
  getLegalBasisDescription,
  getDocumentTypeDescription,
  getFormTypeDescription
};

/**
 * Get enhanced notice type description including legal basis
 */
export function getEnhancedNoticeTypeDescription(tender: any): string | null {
  if (!tender) return null;
  
  // First try to get by subtype ID
  const subtypeId = getNoticeSubtypeId(tender);
  if (subtypeId) {
    const subtype = getNoticeSubtypeById(subtypeId);
    if (subtype) {
      return `${subtype.description} (${getLegalBasisDescription(subtype.legalBasis)})`;
    }
  }
  
  // Try by notice type
  const noticeType = tender["notice-type"];
  if (noticeType) {
    const subtypes = getNoticeSubtypesByNoticeType(noticeType);
    if (subtypes.length > 0) {
      // If multiple subtypes match, return a generic description
      if (subtypes.length > 1) {
        return `${getNoticeTypeDescription(noticeType)}`;
      }
      return `${subtypes[0].description} (${getLegalBasisDescription(subtypes[0].legalBasis)})`;
    }
  }
  
  // Fallback to basic description
  return getNoticeTypeDescription(noticeType);
}

/**
 * Get document type information from tender
 */
export function getDocumentType(tender: any): string | null {
  if (!tender) return null;
  
  // Try to get by subtype ID
  const subtypeId = getNoticeSubtypeId(tender);
  if (subtypeId) {
    const subtype = getNoticeSubtypeById(subtypeId);
    if (subtype) {
      return subtype.documentTypeId;
    }
  }
  
  // Try to infer from notice type
  const noticeType = tender["notice-type"];
  if (noticeType) {
    const subtypes = getNoticeSubtypesByNoticeType(noticeType);
    if (subtypes.length > 0) {
      return subtypes[0].documentTypeId;
    }
  }
  
  return null;
}
